import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { pdfTemplatesAPI } from '../../services/api';
import { 
  Plus, 
  Edit, 
  Trash2, 
  Save, 
  X, 
  Eye,
  CheckCircle,
  FileText,
  Download,
  Settings,
  Move,
  Type,
  Image,
  Layout,
  Palette,
  Code,
  CheckSquare,
  Square,
  Zap,
  Loader2,
  Calculator,
  User,
  Calendar
} from 'lucide-react';

const PDFTemplateManagement = () => {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [newTemplate, setNewTemplate] = useState({
    name: '',
    template_type: 'quote',
    header_content: '',
    footer_content: '',
    body_template: '',
    css_styles: '',
    is_active: false
  });
  const [showAddForm, setShowAddForm] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [activeTab, setActiveTab] = useState('body'); // body, header, footer, styles
  const [showVisualEditor, setShowVisualEditor] = useState(false);
  const [visualEditorMode, setVisualEditorMode] = useState('add'); // 'add' or 'edit'
  const [dragElements, setDragElements] = useState([]);
  const [selectedElement, setSelectedElement] = useState(null);
  const [previewMode, setPreviewMode] = useState(false);
  const canvasRef = useRef(null);

  // Bulk actions state
  const [selectedTemplates, setSelectedTemplates] = useState([]);
  const [selectAll, setSelectAll] = useState(false);
  const [bulkActionLoading, setBulkActionLoading] = useState(false);
  const [showBulkActions, setShowBulkActions] = useState(false);

  // Drag and drop elements available (advanced)
  const availableElements = [
    { id: 'text', label: 'Text Block', icon: Type, type: 'text', defaultContent: 'Enter your text here' },
    { id: 'heading', label: 'Heading', icon: Type, type: 'heading', defaultContent: 'Section Heading' },
    { id: 'image', label: 'Image', icon: Image, type: 'image', defaultContent: 'https://via.placeholder.com/200x60?text=Logo', w: 200, h: 60 },
    { id: 'divider', label: 'Divider', icon: Layout, type: 'divider', defaultContent: '' },
    { id: 'blockContainer', label: 'Block Container', icon: Layout, type: 'blockContainer', defaultContent: 'Drop elements here', w: 500, h: 150 },
    { id: 'flexContainer', label: 'Flexible Container', icon: Layout, type: 'flexContainer', defaultContent: 'Drop 2-3 elements', w: 520, h: 140 },
    { id: 'box', label: 'Box', icon: Layout, type: 'box', defaultContent: '', w: 300, h: 120 },
    { id: 'circle', label: 'Circle', icon: Layout, type: 'circle', defaultContent: '', w: 80, h: 80 },
    { id: 'table', label: 'Generic Table', icon: Layout, type: 'table', defaultContent: 'Table Title' },
    { id: 'itemsTable', label: 'Items Table', icon: Layout, type: 'itemsTable', defaultContent: 'Items' },
    { id: 'totals', label: 'Totals Block', icon: Calculator, type: 'totals', defaultContent: '' },
    { id: 'customer', label: 'Customer Block', icon: User, type: 'customer', defaultContent: '' },
    { id: 'qr', label: 'QR Code', icon: Zap, type: 'qr', defaultContent: '{{paymentQR}}', w: 120, h: 120 },
    { id: 'pageNumber', label: 'Page Number', icon: FileText, type: 'pageNumber', defaultContent: 'Page {{pageNumber}}/{{pageCount}}' },
    { id: 'date', label: 'Current Date', icon: Calendar, type: 'date', defaultContent: '{{currentDate}}' },
    { id: 'variable', label: 'Variable', icon: Code, type: 'variable', defaultContent: '{{projectType}}' },
    { id: 'spacer', label: 'Spacer', icon: Layout, type: 'spacer', defaultContent: '' }
  ];

  const fetchTemplates = async () => {
    try {
      setLoading(true);
      console.log('🔍 Fetching PDF templates...');
      
      const response = await pdfTemplatesAPI.getAll();
      console.log('✅ Templates response:', response);
      
      if (response?.data?.success && Array.isArray(response.data.data)) {
        setTemplates(response.data.data);
      } else {
        console.warn('Invalid response format:', response);
        setTemplates([]);
      }
    } catch (error) {
      console.error('❌ Error fetching templates:', error);
      setMessage({ type: 'error', text: 'Failed to fetch templates' });
      setTemplates([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTemplates();
  }, []);

  const handleAddTemplate = async () => {
    try {
      if (!newTemplate.name || !newTemplate.body_template) {
        setMessage({ type: 'error', text: 'Template name and body template are required' });
        return;
      }

      const response = await pdfTemplatesAPI.create(newTemplate);
      if (response?.data?.success) {
        setMessage({ type: 'success', text: 'Template added successfully' });
        setShowAddForm(false);
        setNewTemplate({
          name: '',
          template_type: 'quote',
          header_content: '',
          footer_content: '',
          body_template: '',
          css_styles: '',
          is_active: false
        });
        fetchTemplates();
      }
    } catch (error) {
      console.error('Error adding template:', error);
      setMessage({ type: 'error', text: error.response?.data?.message || 'Failed to add template' });
    }
  };

  const handleUpdateTemplate = async () => {
    try {
      if (!editingTemplate.name || !editingTemplate.body_template) {
        setMessage({ type: 'error', text: 'Template name and body template are required' });
        return;
      }

      const response = await pdfTemplatesAPI.update(editingTemplate.id, editingTemplate);
      if (response?.data?.success) {
        setMessage({ type: 'success', text: 'Template updated successfully' });
        setEditingTemplate(null);
        fetchTemplates();
      }
    } catch (error) {
      console.error('Error updating template:', error);
      setMessage({ type: 'error', text: error.response?.data?.message || 'Failed to update template' });
    }
  };

  const handleDeleteTemplate = async (id) => {
    if (!window.confirm('Are you sure you want to delete this template?')) {
      return;
    }

    try {
      const response = await pdfTemplatesAPI.delete(id);
      if (response?.data?.success) {
        setMessage({ type: 'success', text: 'Template deleted successfully' });
        fetchTemplates();
    }
    } catch (error) {
      console.error('Error deleting template:', error);
      setMessage({ type: 'error', text: 'Failed to delete template' });
    }
  };

  const handleActivateTemplate = async (id) => {
    try {
      const response = await pdfTemplatesAPI.activate(id);
      if (response?.data?.success) {
        setMessage({ type: 'success', text: 'Template activated successfully' });
        fetchTemplates();
      }
    } catch (error) {
      console.error('Error activating template:', error);
      setMessage({ type: 'error', text: 'Failed to activate template' });
    }
  };

  const clearMessage = () => {
    setMessage({ type: '', text: '' });
  };

  // Bulk actions functions
  const handleSelectTemplate = (templateId) => {
    setSelectedTemplates(prev => {
      const newSelected = prev.includes(templateId)
        ? prev.filter(id => id !== templateId)
        : [...prev, templateId];
      
      setShowBulkActions(newSelected.length > 0);
      setSelectAll(newSelected.length === templates.length);
      return newSelected;
    });
  };

  const handleSelectAll = () => {
    if (selectAll) {
      setSelectedTemplates([]);
      setShowBulkActions(false);
      setSelectAll(false);
    } else {
      const allTemplateIds = templates.map(template => template.id);
      setSelectedTemplates(allTemplateIds);
      setShowBulkActions(true);
      setSelectAll(true);
    }
  };

  const handleBulkStatusUpdate = async (is_active) => {
    if (selectedTemplates.length === 0) return;

    try {
      setBulkActionLoading(true);
      const response = await pdfTemplatesAPI.bulkUpdateStatus(selectedTemplates, is_active);
      
      if (response?.data?.success) {
        setMessage({ 
          type: 'success', 
          text: `${selectedTemplates.length} template${selectedTemplates.length !== 1 ? 's' : ''} ${is_active ? 'activated' : 'deactivated'} successfully` 
        });
        setSelectedTemplates([]);
        setShowBulkActions(false);
        setSelectAll(false);
        fetchTemplates();
      } else {
        setMessage({ type: 'error', text: response?.data?.message || 'Failed to update templates' });
      }
    } catch (error) {
      console.error('Error updating templates:', error);
      setMessage({ type: 'error', text: 'Failed to update templates' });
    } finally {
      setBulkActionLoading(false);
    }
  };

  const handleBulkDelete = async () => {
    if (selectedTemplates.length === 0) return;

    if (!window.confirm(`Are you sure you want to delete ${selectedTemplates.length} template${selectedTemplates.length !== 1 ? 's' : ''}?`)) {
      return;
    }

    try {
      setBulkActionLoading(true);
      const response = await pdfTemplatesAPI.bulkDelete(selectedTemplates);
      
      if (response?.data?.success) {
        setMessage({ 
          type: 'success', 
          text: `${selectedTemplates.length} template${selectedTemplates.length !== 1 ? 's' : ''} deleted successfully` 
        });
        setSelectedTemplates([]);
        setShowBulkActions(false);
        setSelectAll(false);
        fetchTemplates();
      } else {
        setMessage({ type: 'error', text: response?.data?.message || 'Failed to delete templates' });
      }
    } catch (error) {
      console.error('Error deleting templates:', error);
      setMessage({ type: 'error', text: 'Failed to delete templates' });
    } finally {
      setBulkActionLoading(false);
    }
  };

  // Visual Editor Functions
  const handleDragStart = (e, element) => {
    e.dataTransfer.setData('application/json', JSON.stringify(element));
  };

  const handleDragOver = (e) => {
    e.preventDefault();
  };

  const handleDrop = (e) => {
    e.preventDefault();
    const elementData = JSON.parse(e.dataTransfer.getData('application/json'));
    const newElement = {
      id: Date.now(),
      ...elementData,
      content: elementData.defaultContent,
      position: { x: e.clientX, y: e.clientY }
    };
    setDragElements([...dragElements, newElement]);
  };

  const handleElementSelect = (element) => {
    setSelectedElement(element);
  };

  const handleElementUpdate = (elementId, updates) => {
    setDragElements(dragElements.map(el => 
      el.id === elementId ? { ...el, ...updates } : el
    ));
  };

  const handleElementDelete = (elementId) => {
    setDragElements(dragElements.filter(el => el.id !== elementId));
    if (selectedElement?.id === elementId) {
      setSelectedElement(null);
    }
  };

  const generateHTMLFromElements = () => {
    return dragElements.map(element => {
      switch (element.type) {
        case 'text':
          return `<div style="margin-bottom: 15px; line-height: 1.6;">${element.content}</div>`;
        case 'heading':
          return `<h2 style="font-size: 18px; font-weight: bold; color: #3b82f6; margin-bottom: 10px; border-bottom: 2px solid #3b82f6; padding-bottom: 5px;">${element.content}</h2>`;
        case 'blockContainer':
          return `<div style="border:1px dashed #cbd5e1;border-radius:8px;padding:12px;margin:8px 0;width:${element.w||500}px;min-height:${element.h||150}px">${element.content}</div>`;
        case 'flexContainer':
          return `<div style="display:flex;gap:12px;border:1px dashed #cbd5e1;border-radius:8px;padding:12px;margin:8px 0;width:${element.w||520}px;min-height:${element.h||140}px"><div style="flex:1">${element.content}</div><div style="flex:1"></div></div>`;
        case 'image':
          return `<div style="text-align:left;margin:10px 0;"><img src="${element.content}" alt="img" style="max-width:${element.w||200}px;height:auto;" /></div>`;
        case 'table':
          return `<div style="margin-bottom: 20px;">
            <h3 style="font-size: 16px; font-weight: bold; margin-bottom: 10px;">${element.content}</h3>
            <table style="width: 100%; border-collapse: collapse; margin-bottom: 10px;">
              <tr style="background-color: #f8f9fa;">
                <th style="border: 1px solid #ddd; padding: 8px; text-align: left;">Field</th>
                <th style="border: 1px solid #ddd; padding: 8px; text-align: left;">Value</th>
              </tr>
              <tr>
                <td style="border: 1px solid #ddd; padding: 8px;">Project Type</td>
                <td style="border: 1px solid #ddd; padding: 8px;">{{projectType}}</td>
              </tr>
              <tr>
                <td style="border: 1px solid #ddd; padding: 8px;">Estimated Cost</td>
                <td style="border: 1px solid #ddd; padding: 8px;">{{estimatedCost}}</td>
              </tr>
            </table>
          </div>`;
        case 'itemsTable':
          return `<table style="width:100%;border-collapse:collapse;margin:12px 0;">
            <thead>
              <tr style="background:#0b5bd3;color:#fff;">
                <th style="text-align:left;padding:8px;border:1px solid #e5e7eb;">S.NO</th>
                <th style="text-align:left;padding:8px;border:1px solid #e5e7eb;">Item description</th>
                <th style="text-align:right;padding:8px;border:1px solid #e5e7eb;">Qty</th>
                <th style="text-align:right;padding:8px;border:1px solid #e5e7eb;">Price</th>
                <th style="text-align:right;padding:8px;border:1px solid #e5e7eb;">Tax</th>
                <th style="text-align:right;padding:8px;border:1px solid #e5e7eb;">Total</th>
              </tr>
            </thead>
            <tbody>
              {{#each items}}
                <tr>
                  <td style="padding:8px;border:1px solid #e5e7eb;">{{inc @index}}</td>
                  <td style="padding:8px;border:1px solid #e5e7eb;">{{this.title}}<br/><small>{{this.short_description}}</small></td>
                  <td style="padding:8px;border:1px solid #e5e7eb;text-align:right;">{{this.quantity}}</td>
                  <td style="padding:8px;border:1px solid #e5e7eb;text-align:right;">{{this.unit_price}}</td>
                  <td style="padding:8px;border:1px solid #e5e7eb;text-align:right;">{{this.tax_rate}}%</td>
                  <td style="padding:8px;border:1px solid #e5e7eb;text-align:right;">{{this.total}}</td>
                </tr>
              {{/each}}
            </tbody>
          </table>`;
        case 'totals':
          return `<div style="width:100%;display:flex;justify-content:flex-end;margin-top:8px;">
            <div style="min-width:260px;">
              <div style="display:flex;justify-content:space-between;padding:4px 0;">
                <span>Sub Total</span><span>{{subtotal}}</span>
              </div>
              <div style="display:flex;justify-content:space-between;padding:4px 0;">
                <span>Vat & Tax</span><span>{{tax_amount}}</span>
              </div>
              <div style="display:flex;justify-content:space-between;padding:4px 0;">
                <span>Discount</span><span>- {{discount_value}}</span>
              </div>
              <div style="margin-top:6px;background:#0b5bd3;color:#fff;padding:8px 10px;display:flex;justify-content:space-between;">
                <strong>Grand Total</strong><strong>{{total_amount}}</strong>
              </div>
            </div>
          </div>`;
        case 'customer':
          return `<div style="border:1px solid #e5e7eb;padding:10px;border-radius:6px;margin:10px 0;">
            <div style="font-weight:bold;margin-bottom:6px;">Bill To:</div>
            <div>{{customer_name}}</div>
            <div>{{customer_email}}</div>
            <div>{{customer_phone}}</div>
            <div style="white-space:pre-line;">{{billing_address}}</div>
          </div>`;
        case 'box':
          return `<div style="border:1px solid #e5e7eb;border-radius:6px;width:${element.w||300}px;height:${element.h||120}px"></div>`;
        case 'circle':
          return `<div style="border:1px solid #e5e7eb;border-radius:9999px;width:${element.w||80}px;height:${element.h||80}px"></div>`;
        case 'qr':
          return `<div style="width:${element.w||120}px;height:${element.h||120}px;border:1px dashed #cbd5e1;display:flex;align-items:center;justify-content:center;">QR: ${element.content}</div>`;
        case 'pageNumber':
          return `<div style="text-align:right;color:#6b7280;">${element.content}</div>`;
        case 'date':
          return `<div style="color:#6b7280;">${element.content}</div>`;
        case 'image':
          return `<div style="text-align: center; margin: 20px 0;">
            <img src="{{companyLogo}}" alt="${element.content}" style="max-width: 200px; height: auto;" />
          </div>`;
        case 'divider':
          return `<hr style="border: none; border-top: 1px solid #ddd; margin: 20px 0;" />`;
        case 'spacer':
          return `<div style="height: 20px;"></div>`;
        case 'variable':
          return `<span style="color: #3b82f6; font-weight: bold;">${element.content}</span>`;
        default:
          return `<div>${element.content}</div>`;
      }
    }).join('\n');
  };

  const saveVisualTemplate = async () => {
    try {
      const htmlContent = generateHTMLFromElements();
      const templateData = {
        name: newTemplate.name || 'Visual Template',
        template_type: newTemplate.template_type,
        header_content: newTemplate.header_content,
        footer_content: newTemplate.footer_content,
        body_template: htmlContent,
        css_styles: newTemplate.css_styles,
        is_active: newTemplate.is_active
      };

      if (visualEditorMode === 'add') {
        const response = await pdfTemplatesAPI.create(templateData);
        if (response?.data?.success) {
          setMessage({ type: 'success', text: 'Visual template created successfully' });
          setShowVisualEditor(false);
          setDragElements([]);
          fetchTemplates();
        }
      } else {
        const response = await pdfTemplatesAPI.update(editingTemplate.id, templateData);
        if (response?.data?.success) {
          setMessage({ type: 'success', text: 'Visual template updated successfully' });
          setShowVisualEditor(false);
          setDragElements([]);
          setEditingTemplate(null);
          fetchTemplates();
        }
      }
    } catch (error) {
      console.error('Error saving visual template:', error);
      setMessage({ type: 'error', text: 'Failed to save visual template' });
    }
  };

  const openVisualEditor = (mode = 'add', template = null) => {
    setVisualEditorMode(mode);
    setShowVisualEditor(true);
    setDragElements([]);
    setSelectedElement(null);
    setPreviewMode(false);
    
    if (mode === 'edit' && template) {
      // Parse existing template HTML into visual elements
      // This is a simplified version - in a real implementation, you'd need more sophisticated parsing
      setNewTemplate({
        name: template.name,
        template_type: template.template_type,
        header_content: template.header_content,
        footer_content: template.footer_content,
        body_template: template.body_template,
        css_styles: template.css_styles,
        is_active: template.is_active
      });
    }
  };

  const renderVisualEditor = () => {
    const modal = (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[1000]">
        <div className="bg-white dark:bg-gray-800 rounded-lg w-[95vw] h-[90vh] flex flex-col shadow-2xl">
          {/* Header */}
          <div className="flex justify-between items-center p-4 border-b border-gray-200 dark:border-gray-700">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
              Visual PDF Template Editor
            </h3>
            <div className="flex gap-2">
              <button
                onClick={() => setPreviewMode(!previewMode)}
                className="px-3 py-1 text-sm bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded hover:bg-gray-200"
              >
                {previewMode ? 'Edit' : 'Preview'}
              </button>
              <button
                onClick={() => setShowVisualEditor(false)}
                className="px-3 py-1 text-sm bg-red-100 text-red-700 rounded hover:bg-red-200"
              >
                Cancel
              </button>
              <button
                onClick={saveVisualTemplate}
                className="px-3 py-1 text-sm bg-primary-600 text-white rounded hover:bg-primary-700"
              >
                Save Template
              </button>
            </div>
          </div>

          <div className="flex flex-1 overflow-hidden">
            {/* Sidebar - Elements */}
            <div className="w-64 bg-gray-50 dark:bg-gray-900 p-4 border-r border-gray-200 dark:border-gray-700">
              <h4 className="font-semibold text-gray-900 dark:text-white mb-4">Elements</h4>
              <div className="space-y-2">
                {availableElements.map((element) => (
                  <div
                    key={element.id}
                    draggable
                    onDragStart={(e) => handleDragStart(e, element)}
                    className="flex items-center gap-2 p-2 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded cursor-move hover:bg-gray-50 dark:hover:bg-gray-700"
                  >
                    <element.icon className="w-4 h-4 text-gray-500" />
                    <span className="text-sm text-gray-700 dark:text-gray-300">{element.label}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Main Canvas */}
            <div className="flex-1 flex flex-col">
              <div className="p-4 border-b border-gray-200 dark:border-gray-700">
                <input
                  type="text"
                  value={newTemplate.name}
                  onChange={(e) => setNewTemplate({ ...newTemplate, name: e.target.value })}
                  placeholder="Template Name"
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                />
      </div>

              <div
                ref={canvasRef}
                onDragOver={handleDragOver}
                onDrop={handleDrop}
                className="flex-1 bg-white dark:bg-gray-800 p-4 sm:p-6 overflow-y-auto"
                style={{ minHeight: '500px' }}
              >
                {previewMode ? (
                  <div className="max-w-4xl mx-auto bg-white border border-gray-200 rounded-lg p-8 shadow-sm">
                    <div dangerouslySetInnerHTML={{ __html: generateHTMLFromElements() }} />
                  </div>
                ) : (
                  <div className="max-w-4xl mx-auto bg-white border border-gray-200 rounded-lg p-8 shadow-sm min-h-[600px]">
                    {dragElements.length === 0 ? (
                      <div className="text-center text-gray-500 dark:text-gray-400 py-20">
                        <Move className="w-12 h-12 mx-auto mb-4 text-gray-300" />
                        <p>Drag elements from the sidebar to start building your template</p>
                      </div>
                    ) : (
                      dragElements.map((element) => (
                        <div
                          key={element.id}
                          onClick={() => handleElementSelect(element)}
                          className={`relative p-4 border-2 border-dashed border-gray-300 rounded-lg mb-4 cursor-pointer hover:border-primary-500 ${
                            selectedElement?.id === element.id ? 'border-primary-500 bg-primary-50' : ''
                          }`}
                        >
                          <div className="flex items-center justify-between mb-2">
                            <span className="text-xs font-medium text-gray-500 uppercase">
                              {element.label}
                            </span>
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleElementDelete(element.id);
                              }}
                              className="text-red-500 hover:text-red-700"
                            >
                              <X className="w-4 h-4" />
                            </button>
                          </div>
                          <div
                            contentEditable
                            suppressContentEditableWarning
                            onBlur={(e) => handleElementUpdate(element.id, { content: e.target.textContent })}
                            className="outline-none"
                          >
                            {element.content}
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                )}
              </div>
            </div>

            {/* Properties Panel */}
            {selectedElement && (
              <div className="w-64 bg-gray-50 dark:bg-gray-900 p-4 border-l border-gray-200 dark:border-gray-700">
                <h4 className="font-semibold text-gray-900 dark:text-white mb-4">Properties</h4>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Content
                    </label>
                    <textarea
                      value={selectedElement.content}
                      onChange={(e) => handleElementUpdate(selectedElement.id, { content: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent dark:bg-gray-700 dark:text-white text-sm"
                      rows="3"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Type
                    </label>
                    <span className="text-sm text-gray-600 dark:text-gray-400">{selectedElement.label}</span>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    );
    return createPortal(modal, document.body);
  };

  const renderTemplateForm = (template, isEditing = false) => {
    const currentTemplate = isEditing ? template : newTemplate;
    const setCurrentTemplate = isEditing ? setEditingTemplate : setNewTemplate;

    return (
      <div className="space-y-6">
        {/* Basic Info */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Template Name
            </label>
            <input
              type="text"
              value={currentTemplate.name}
              onChange={(e) => setCurrentTemplate({ ...currentTemplate, name: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
              placeholder="Enter template name"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Template Type
            </label>
            <select
              value={currentTemplate.template_type}
              onChange={(e) => setCurrentTemplate({ ...currentTemplate, template_type: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
            >
              <option value="quote">Quote</option>
              <option value="invoice">Invoice</option>
              <option value="proposal">Proposal</option>
            </select>
          </div>
        </div>

        {/* Template Content Tabs */}
        <div>
          <div className="border-b border-gray-200 dark:border-gray-700">
            <nav className="-mb-px flex space-x-8">
              {[
                { id: 'body', label: 'Body Template', icon: FileText },
                { id: 'header', label: 'Header', icon: Eye },
                { id: 'footer', label: 'Footer', icon: Eye },
                { id: 'styles', label: 'CSS Styles', icon: Settings }
              ].map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`py-2 px-1 border-b-2 font-medium text-sm flex items-center gap-2 ${
                    activeTab === tab.id
                      ? 'border-primary-500 text-primary-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  <tab.icon className="w-4 h-4" />
                  {tab.label}
                </button>
              ))}
            </nav>
          </div>

          <div className="mt-4">
            {activeTab === 'body' && (
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Body Template (Handlebars)
                </label>
                <textarea
                  value={currentTemplate.body_template}
                  onChange={(e) => setCurrentTemplate({ ...currentTemplate, body_template: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent dark:bg-gray-700 dark:text-white font-mono text-sm"
                  rows="20"
                  placeholder="Enter Handlebars template with variables like {{projectType}}, {{estimatedCost}}, etc."
                />
                <p className="text-xs text-gray-500 mt-2">
                  Available variables: {'{{projectType}}'}, {'{{features}}'}, {'{{timeline}}'}, {'{{complexity}}'}, {'{{estimatedCost}}'}, {'{{estimatedTime}}'}, {'{{contact}}'}, {'{{generatedAt}}'}, {'{{companyInfo}}'}
                </p>
              </div>
            )}

            {activeTab === 'header' && (
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Header Content (HTML)
                </label>
                <textarea
                  value={currentTemplate.header_content}
                  onChange={(e) => setCurrentTemplate({ ...currentTemplate, header_content: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent dark:bg-gray-700 dark:text-white font-mono text-sm"
                  rows="8"
                  placeholder="Enter HTML header content"
                />
              </div>
            )}

            {activeTab === 'footer' && (
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Footer Content (HTML)
                </label>
                <textarea
                  value={currentTemplate.footer_content}
                  onChange={(e) => setCurrentTemplate({ ...currentTemplate, footer_content: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent dark:bg-gray-700 dark:text-white font-mono text-sm"
                  rows="8"
                  placeholder="Enter HTML footer content"
                />
              </div>
            )}

            {activeTab === 'styles' && (
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  CSS Styles
                </label>
                <textarea
                  value={currentTemplate.css_styles}
                  onChange={(e) => setCurrentTemplate({ ...currentTemplate, css_styles: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent dark:bg-gray-700 dark:text-white font-mono text-sm"
                  rows="12"
                  placeholder="Enter CSS styles for the template"
                />
            </div>
          )}
        </div>
      </div>

        {/* Active Status */}
        <div className="flex items-center">
          <label className="flex items-center">
            <input
              type="checkbox"
              checked={currentTemplate.is_active}
              onChange={(e) => setCurrentTemplate({ ...currentTemplate, is_active: e.target.checked })}
              className="mr-2"
            />
            <span className="text-sm text-gray-700 dark:text-gray-300">Set as active template</span>
          </label>
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="p-4 sm:p-6">
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-white flex items-center">
              PDF Template Management
            </h1>
            <p className="text-gray-600 dark:text-gray-400 mt-1">
              Manage PDF templates for quote downloads
            </p>
          </div>
          <div className="flex gap-3">
            <button
              onClick={() => openVisualEditor('add')}
              className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg flex items-center gap-2 transition-colors"
            >
              <Palette className="w-4 h-4" />
              Visual Editor
            </button>
            <button
              onClick={() => setShowAddForm(true)}
              className="bg-primary-600 hover:bg-primary-700 text-white px-4 py-2 rounded-lg flex items-center gap-2 transition-colors"
            >
              <Plus className="w-4 h-4" />
              Add Template
            </button>
          </div>
        </div>
      </div>

      {/* Message */}
      {message.text && (
        <div className={`p-4 rounded-lg mb-6 ${
          message.type === 'success' 
            ? 'bg-green-100 text-green-700 border border-green-200' 
            : 'bg-red-100 text-red-700 border border-red-200'
        }`}>
          <div className="flex justify-between items-center">
            <span>{message.text}</span>
            <button onClick={clearMessage} className="text-gray-500 hover:text-gray-700">
              <X className="w-4 h-4" />
            </button>
              </div>
            </div>
      )}

      {/* Add Template Form */}
      {showAddForm && (
        <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4 sm:p-6 mb-6">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Add New Template</h3>
            <button
              onClick={() => setShowAddForm(false)}
              className="text-gray-500 hover:text-gray-700"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
          
          {renderTemplateForm(newTemplate, false)}
          
          <div className="flex justify-end gap-3 mt-6">
            <button
              onClick={() => setShowAddForm(false)}
              className="px-4 py-2 text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              onClick={handleAddTemplate}
              className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700"
            >
              Add Template
            </button>
          </div>
                </div>
      )}

      {/* Bulk Actions */}
      {showBulkActions && (
        <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 p-4 rounded-lg mb-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <span className="text-sm font-medium text-blue-900 dark:text-blue-100">
                {selectedTemplates.length} template{selectedTemplates.length !== 1 ? 's' : ''} selected
              </span>
              
              <div className="flex items-center space-x-2">
                <button
                  onClick={() => handleBulkStatusUpdate(true)}
                  disabled={bulkActionLoading}
                  className="px-3 py-1 text-xs bg-green-600 hover:bg-green-700 text-white rounded-md flex items-center disabled:opacity-50"
                >
                  {bulkActionLoading ? <Loader2 className="h-3 w-3 mr-1 animate-spin" /> : <CheckCircle className="h-3 w-3 mr-1" />}
                  Activate
                </button>
                
                <button
                  onClick={() => handleBulkStatusUpdate(false)}
                  disabled={bulkActionLoading}
                  className="px-3 py-1 text-xs bg-red-600 hover:bg-red-700 text-white rounded-md flex items-center disabled:opacity-50"
                >
                  {bulkActionLoading ? <Loader2 className="h-3 w-3 mr-1 animate-spin" /> : <X className="h-3 w-3 mr-1" />}
                  Deactivate
                </button>
                
                <button
                  onClick={handleBulkDelete}
                  disabled={bulkActionLoading}
                  className="px-3 py-1 text-xs bg-red-600 hover:bg-red-700 text-white rounded-md flex items-center disabled:opacity-50"
                >
                  {bulkActionLoading ? <Loader2 className="h-3 w-3 mr-1 animate-spin" /> : <Trash2 className="h-3 w-3 mr-1" />}
                  Delete
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Templates List */}
      <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
        <div className="p-4 sm:p-6">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
              PDF Templates ({templates.length})
            </h3>
            <button
              onClick={handleSelectAll}
              className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white"
            >
              {selectAll ? (
                <CheckSquare className="h-4 w-4 text-blue-600" />
              ) : (
                <Square className="h-4 w-4 text-gray-400" />
              )}
              Select All
            </button>
          </div>
          
          {templates.length === 0 ? (
            <p className="text-gray-500 dark:text-gray-400 text-center py-8">
              No templates found. Add your first template to get started.
            </p>
          ) : (
            <div className="space-y-4">
              {templates.map((template) => (
                <div
                  key={template.id}
                  className={`border border-gray-200 dark:border-gray-700 rounded-lg p-4 ${
                    template.is_active ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/20' : ''
                  }`}
                >
                  {editingTemplate?.id === template.id ? (
                    // Edit Form
                    <div className="space-y-4">
                      {renderTemplateForm(editingTemplate, true)}
                      
                      <div className="flex justify-end gap-3">
                        <button
                          onClick={() => setEditingTemplate(null)}
                          className="px-4 py-2 text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50"
                        >
                          Cancel
                        </button>
                        <button
                          onClick={handleUpdateTemplate}
                          className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700"
                        >
                          Save Changes
                        </button>
                      </div>
                    </div>
                  ) : (
                    // Display Mode
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        <button
                          onClick={() => handleSelectTemplate(template.id)}
                          className="flex items-center"
                        >
                          {selectedTemplates.includes(template.id) ? (
                            <CheckSquare className="h-4 w-4 text-blue-600" />
                          ) : (
                            <Square className="h-4 w-4 text-gray-400" />
                          )}
                        </button>
                        <div className="flex items-center gap-2">
                          <FileText className="w-5 h-5 text-gray-600 dark:text-gray-400" />
                          <div>
                            <h4 className="font-medium text-gray-900 dark:text-white">
                              {template.name}
                            </h4>
                            <p className="text-sm text-gray-600 dark:text-gray-400">
                              Type: {template.template_type} • {template.is_active ? 'Active' : 'Inactive'}
                            </p>
                          </div>
                        </div>
                        {template.is_active && (
                          <div className="flex items-center gap-1 text-green-600">
                            <CheckCircle className="w-4 h-4" />
                            <span className="text-sm font-medium">Active</span>
                          </div>
                        )}
                      </div>
                      
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => openVisualEditor('edit', template)}
                          className="p-2 text-gray-600 hover:text-green-600 hover:bg-gray-100 rounded-lg"
                          title="Edit with Visual Editor"
                        >
                          <Palette className="w-4 h-4" />
                        </button>
                        {!template.is_active && (
                          <button
                            onClick={() => handleActivateTemplate(template.id)}
                            className="p-2 text-gray-600 hover:text-green-600 hover:bg-gray-100 rounded-lg"
                            title="Activate template"
                          >
                            <CheckCircle className="w-4 h-4" />
                          </button>
                        )}
                        <button
                          onClick={() => setEditingTemplate(template)}
                          className="p-2 text-gray-600 hover:text-primary-600 hover:bg-gray-100 rounded-lg"
                          title="Edit template"
                        >
                          <Edit className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleDeleteTemplate(template.id)}
                          className="p-2 text-gray-600 hover:text-red-600 hover:bg-gray-100 rounded-lg"
                          title="Delete template"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Visual Editor Modal */}
      {showVisualEditor && renderVisualEditor()}
    </div>
  );
};

export default PDFTemplateManagement; 